import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../lib/api.js";
import { getActiveProfileId, setActiveProfileId } from "../lib/profile.js";

export function History(): JSX.Element {
  const profilesQ = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });
  const activeId = getActiveProfileId();
  const [profileId, setProfileId] = useState<string | null>(activeId);

  useEffect(() => {
    if (!profileId && profilesQ.data?.profiles[0]) {
      const id = profilesQ.data.profiles[0].id;
      setActiveProfileId(id);
      setProfileId(id);
    }
  }, [profilesQ.data, profileId]);

  const historyQ = useQuery({
    queryKey: ["history", profileId],
    queryFn: () => Api.history(profileId!),
    enabled: !!profileId,
  });

  if (!profileId) {
    return (
      <div>
        <h1 className="font-display text-3xl text-ivory">History</h1>
        <div className="ornament-rule" />
        <div className="card p-8 text-parchment">Pick a profile first.</div>
      </div>
    );
  }

  const items = historyQ.data?.items ?? [];
  const grouped = groupByDay(items);

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">History</h1>
        <span className="section-label">{items.length} items</span>
      </div>
      <div className="ornament-rule" />

      {historyQ.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-parchment text-center font-display tracking-smallcaps">
          Nothing yet. Watch something and it'll show up here.
        </div>
      ) : (
        grouped.map(({ day, rows }) => (
          <section key={day} className="mb-8">
            <div className="section-label mb-3">{day}</div>
            <ul className="space-y-1">
              {rows.map((it) => (
                <li key={it.id}>
                  <Link
                    to={`/watch/${encodeURIComponent(it.id)}`}
                    className="card flex items-center gap-4 p-3 hover:bg-elev/60 transition-colors group"
                  >
                    <div className="w-28 aspect-video bg-elev rounded overflow-hidden flex-shrink-0">
                      {it.thumbnail_url ? (
                        <img
                          src={it.thumbnail_url}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                        />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-ivory truncate">{it.title}</div>
                      <div className="text-parchment text-xs mt-1">
                        {new Date(it.last_seen_at).toLocaleTimeString()} ·{" "}
                        <EventLabel ev={it.last_event} />
                        {it.last_position_s > 0 && it.duration_s ? (
                          <> · {Math.round((it.last_position_s / it.duration_s) * 100)}% in</>
                        ) : null}
                      </div>
                    </div>
                    <ProgressBadge ev={it.last_event} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function EventLabel({ ev }: { ev: string }): JSX.Element {
  const map: Record<string, { label: string; color: string }> = {
    completed: { label: "completed", color: "text-jade" },
    viewed:    { label: "viewed",    color: "text-gold" },
    skipped:   { label: "skipped",   color: "text-parchment" },
    abandoned: { label: "abandoned", color: "text-vermilionHi" },
  };
  const m = map[ev] ?? { label: ev, color: "text-parchment" };
  return <span className={m.color}>{m.label}</span>;
}

function ProgressBadge({ ev }: { ev: string }): JSX.Element | null {
  if (ev === "completed") {
    return <span className="px-2 py-0.5 rounded-full bg-jade/20 border border-jade/50 text-jade text-xs font-display tracking-smallcaps">DONE</span>;
  }
  if (ev === "abandoned") {
    return <span className="px-2 py-0.5 rounded-full bg-vermilion/20 border border-vermilion/50 text-vermilionHi text-xs font-display tracking-smallcaps">LEFT</span>;
  }
  return null;
}

function groupByDay<T extends { last_seen_at: string }>(items: T[]): { day: string; rows: T[] }[] {
  const out = new Map<string, T[]>();
  for (const it of items) {
    const d = new Date(it.last_seen_at);
    const key = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    const arr = out.get(key) ?? [];
    arr.push(it);
    out.set(key, arr);
  }
  return Array.from(out.entries()).map(([day, rows]) => ({ day, rows }));
}
